import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { login as loginApi } from "@/api/auth";
import { setUser, type AuthUser } from "@/utils/auth";

type FieldName = "account" | "password" | "captcha";
type FieldStatus = "idle" | "error" | "success";

interface FieldState {
  value: string;
  error: string;
  status: FieldStatus;
  touched: boolean;
}

function createField(value = ""): FieldState {
  return { value, error: "", status: "idle", touched: false };
}

function generateCaptcha() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "";
  for (let i = 0; i < 4; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

function validateAccount(value: string): string {
  const v = value.trim();
  if (!v) return "请输入账号";
  if (v.length < 2) return "账号长度不能少于2位";
  if (v.length > 24) return "账号长度不能超过24位";
  return "";
}

function validatePassword(value: string): string {
  if (!value) return "请输入密码";
  if (value.length < 6) return "密码长度不能少于6位";
  if (value.length > 36) return "密码长度不能超过36位";
  return "";
}

function validateCaptcha(value: string, code: string, required: boolean): string {
  if (!required) return "";
  if (!value.trim()) return "请输入验证码";
  if (value.toUpperCase() !== code.toUpperCase()) return "验证码错误，请重新输入";
  return "";
}

function fieldClass(status: FieldStatus) {
  if (status === "error") {
    return "border-red-400 bg-red-50/30 focus:border-red-500 focus:ring-red-100";
  }
  if (status === "success") {
    return "border-green-500 bg-green-50/30 focus:border-green-500 focus:ring-green-100";
  }
  return "border-background-300 focus:border-accent-400 focus:ring-accent-100";
}

function iconClass(status: FieldStatus) {
  if (status === "error") return "text-red-500";
  if (status === "success") return "text-green-500";
  return "text-foreground-400";
}

export default function Login() {
  const navigate = useNavigate();
  const [account, setAccount] = useState<FieldState>(createField());
  const [password, setPassword] = useState<FieldState>(createField());
  const [captcha, setCaptcha] = useState<FieldState>(createField());
  const [captchaCode, setCaptchaCode] = useState(() => generateCaptcha());
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formMessage, setFormMessage] = useState<{ type: "error" | "success"; text: string } | null>(null);

  const refreshCaptcha = useCallback(() => {
    setCaptchaCode(generateCaptcha());
    setCaptcha((prev) => ({ ...prev, value: "", error: "", status: "idle" }));
  }, []);

  const applyValidation = useCallback(
    (name: FieldName, value: string, touched: boolean): FieldState => {
      let error = "";
      if (name === "account") error = validateAccount(value);
      if (name === "password") error = validatePassword(value);
      if (name === "captcha") error = validateCaptcha(value, captchaCode, showCaptcha);

      // 空值：未触碰保持 idle；已触碰显示错误
      // 有值：合法 success，不合法 error
      let status: FieldStatus = "idle";
      if (!value) {
        status = touched && error ? "error" : "idle";
      } else if (error) {
        status = "error";
      } else {
        status = "success";
      }

      return {
        value,
        error: status === "error" ? error : "",
        status,
        touched,
      };
    },
    [captchaCode, showCaptcha],
  );

  /** 输入即校验；失焦标记 touched */
  const updateField = (name: FieldName, value: string, touch = false) => {
    const setter = name === "account" ? setAccount : name === "password" ? setPassword : setCaptcha;
    setter((prev) => applyValidation(name, value, touch || prev.touched || !!value));
    setFormMessage(null);
  };

  const validateAll = () => {
    const nextAccount = applyValidation("account", account.value, true);
    const nextPassword = applyValidation("password", password.value, true);
    const nextCaptcha = applyValidation("captcha", captcha.value, true);
    setAccount(nextAccount);
    setPassword(nextPassword);
    setCaptcha(nextCaptcha);
    return !nextAccount.error && !nextPassword.error && !nextCaptcha.error;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormMessage(null);

    if (!validateAll()) {
      setFormMessage({ type: "error", text: "请先完善并修正表单信息" });
      return;
    }

    setLoading(true);
    try {
      const res = await loginApi({
        account: account.value.trim(),
        password: password.value,
      });

      // error === false / 0 表示成功
      const ok = res.error === false || Number(res.error) === 0;

      if (!ok) {
        const serverMsg = res.message || res.msg || "登录失败";
        setFormMessage({ type: "error", text: serverMsg });

        if (res.errors) {
          if (res.errors.account?.[0]) {
            setAccount((prev) => ({
              ...prev,
              error: res.errors!.account![0],
              status: "error",
              touched: true,
            }));
          }
          if (res.errors.password?.[0]) {
            setPassword((prev) => ({
              ...prev,
              error: res.errors!.password![0],
              status: "error",
              touched: true,
            }));
          }
        }

        setShowCaptcha(true);
        refreshCaptcha();
        return;
      }

      const member = res.data?.member;
      const token = res.data?.token;
      if (!member || !token) {
        setFormMessage({ type: "error", text: "登录响应数据异常" });
        return;
      }

      const user: AuthUser = {
        ...member,
        id: member.id,
        account: member.account,
        token,
      };
      setUser(user);

      setFormMessage({ type: "success", text: res.message || "登录成功" });
      setAccount((prev) => ({ ...prev, status: "success", error: "" }));
      setPassword((prev) => ({ ...prev, status: "success", error: "" }));

      setTimeout(() => {
        navigate("/goods", { replace: true });
      }, 400);
    } catch {
      setFormMessage({ type: "error", text: "网络异常，请稍后重试" });
      setShowCaptcha(true);
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-8 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            'url("https://cdn.yyg120.com/FgJR_VUzKhTcixFZwG4IQd6quxyH")',
        }}
      />
      <div className="absolute inset-0 bg-black/50" />

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <img
            src="https://cdn.yyg120.com/Fo_BZtyQ7IYqYrZdSexcrUnft1PI"
            alt="邕宜购"
            className="w-20 h-20 rounded-2xl object-cover mx-auto mb-5"
          />
          <h1 className="text-3xl font-bold text-white tracking-tight">邕宜购</h1>
          <p className="text-sm text-white/70 mt-2">医院物资采购平台</p>
        </div>

        <div
          className="bg-white rounded-2xl border border-background-200/80 p-8"
          style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}
        >
          <h2 className="text-base font-semibold text-foreground-900 mb-6 text-center">账号登录</h2>

          <form onSubmit={handleLogin} className="space-y-4" noValidate>
            <div>
              <label className="block text-xs text-foreground-600 mb-1.5 font-medium">账号</label>
              <div className="relative">
                <i className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ri-user-line ${iconClass(account.status)}`} />
                <input
                  type="text"
                  name="account"
                  autoComplete="username"
                  value={account.value}
                  onChange={(e) => updateField("account", e.target.value)}
                  onBlur={(e) => updateField("account", e.currentTarget.value, true)}
                  className={`w-full pl-9 pr-9 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 transition-all ${fieldClass(account.status)}`}
                  placeholder="请输入账号"
                />
                {account.status === "success" && (
                  <i className="absolute right-3 top-1/2 -translate-y-1/2 ri-checkbox-circle-fill text-green-500 text-base" />
                )}
                {account.status !== "success" && account.value && (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="清空账号"
                    onClick={() => updateField("account", "", account.touched)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        updateField("account", "", account.touched);
                      }
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full bg-foreground-200/50 hover:bg-foreground-300/50 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xs" />
                  </span>
                )}
              </div>
              {account.error ? (
                <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line" />
                  {account.error}
                </p>
              ) : null}
            </div>

            <div>
              <label className="block text-xs text-foreground-600 mb-1.5 font-medium">密码</label>
              <div className="relative">
                <i className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ri-lock-line ${iconClass(password.status)}`} />
                <input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={password.value}
                  onChange={(e) => updateField("password", e.target.value)}
                  onBlur={(e) => updateField("password", e.currentTarget.value, true)}
                  className={`w-full pl-9 pr-9 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 transition-all ${fieldClass(password.status)}`}
                  placeholder="请输入密码"
                />
                {password.status === "success" && (
                  <i className="absolute right-3 top-1/2 -translate-y-1/2 ri-checkbox-circle-fill text-green-500 text-base" />
                )}
                {password.status !== "success" && password.value && (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="清空密码"
                    onClick={() => updateField("password", "", password.touched)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        updateField("password", "", password.touched);
                      }
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full bg-foreground-200/50 hover:bg-foreground-300/50 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xs" />
                  </span>
                )}
              </div>
              {password.error ? (
                <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line" />
                  {password.error}
                </p>
              ) : null}
            </div>

            {showCaptcha && (
              <div>
                <label className="block text-xs text-foreground-600 mb-1.5 font-medium">验证码</label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={captcha.value}
                    onChange={(e) => updateField("captcha", e.target.value.toUpperCase())}
                    onBlur={(e) => updateField("captcha", e.currentTarget.value.toUpperCase(), true)}
                    maxLength={4}
                    className={`flex-1 px-3 py-2.5 rounded-lg border text-sm uppercase tracking-widest text-center outline-none focus:ring-2 transition-all ${fieldClass(captcha.status)}`}
                    placeholder="请输入验证码"
                    autoComplete="off"
                  />
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="刷新验证码"
                    onClick={refreshCaptcha}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        refreshCaptcha();
                      }
                    }}
                    className="flex-shrink-0 w-[120px] h-[42px] rounded-lg border border-background-300 bg-background-100 flex items-center justify-center overflow-hidden relative group cursor-pointer"
                    title="点击刷新验证码"
                  >
                    <span className="relative z-10 text-lg font-bold tracking-[0.3em] select-none text-foreground-800">
                      {captchaCode.split("").map((char, i) => (
                        <span
                          key={`${char}-${i}`}
                          className="inline-block"
                          style={{
                            transform: `rotate(${(i - 1.5) * 8}deg) translateY(${i % 2 === 0 ? -2 : 2}px)`,
                            fontSize: `${18 + (i % 3) * 2}px`,
                            color: i % 2 === 0 ? "oklch(var(--primary-600))" : "oklch(var(--accent-600))",
                          }}
                        >
                          {char}
                        </span>
                      ))}
                    </span>
                    <span className="absolute inset-0 bg-background-50/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20">
                      <i className="ri-refresh-line text-foreground-500 text-lg" />
                    </span>
                  </div>
                </div>
                {captcha.error ? (
                  <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                    <i className="ri-error-warning-line" />
                    {captcha.error}
                  </p>
                ) : captcha.status === "success" ? (
                  <p className="mt-1.5 text-xs text-green-600 flex items-center gap-1">
                    <i className="ri-check-line" />
                    验证码正确
                  </p>
                ) : null}
              </div>
            )}

            {formMessage && (
              <div
                className={`p-3 rounded-lg text-xs flex items-center gap-2 border ${
                  formMessage.type === "success"
                    ? "bg-accent-50 border-accent-300 text-accent-700"
                    : "bg-red-50 border-red-300 text-red-600"
                }`}
              >
                <i
                  className={`flex-shrink-0 ${
                    formMessage.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"
                  }`}
                />
                {formMessage.text}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm transition-colors disabled:opacity-60 whitespace-nowrap flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  登录中...
                </>
              ) : (
                <>
                  <i className="ri-login-box-line" />
                  登 录
                </>
              )}
            </button>

            <input
              type="text"
              name="company_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="company_alt"
            />
          </form>
        </div>

        <p className="text-center text-xs text-white/60 mt-5">
          还没有账号？
          <span
            role="button"
            tabIndex={0}
            className="text-white hover:text-white/80 ml-1 font-medium cursor-pointer"
          >
            联系管理员开通
          </span>
        </p>

        <div className="text-center mt-5 space-y-2">
          <p className="text-xs text-white/50">
            <span className="font-medium text-white/70">广西邕宜购供应链信息技术有限公司</span>
            <span className="mx-2 text-white/30">|</span>
            <span>&copy; 2026 邕宜购</span>
          </p>
          <p className="text-xs text-white/40">
            <a
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white/70 transition-colors"
            >
              ICP证: 桂ICP备2024033029号
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
